import { FC, useEffect, useRef } from 'react'
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material'
import {
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material'
import {
  PrintJobType,
  useCreatePrintJobMutation,
  useFetchPrintJobsQuery,
  useDownloadPrintJobLazyQuery
} from '../../../../../generated'
import { GRAPHQL_QUERY_POLICY } from '../../../../constants'
import { Alert } from '../../../../components/common/Alert'
import { formatDateTime } from '../../../../utils'

const POLL_INTERVAL_MS = 15000

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: '#9E9E9E',
  IN_PROGRESS: '#0288D1',
  COMPLETED: '#2E7D32',
  FAILED: '#D32F2F',
  CANCELLED: '#757575'
}

const STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Queued',
  IN_PROGRESS: 'Generating',
  COMPLETED: 'Ready',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled'
}

interface BatchPdfWidgetProps {
  batchId: number
  totalBills: number
}

export const BatchPdfWidget: FC<BatchPdfWidgetProps> = ({ batchId, totalBills }) => {
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { data, refetch } = useFetchPrintJobsQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: {
      input: {
        batchId,
        printJobType: PrintJobType.PrintBatchAll,
        paginationOptions: { page: 1, limit: 1 }
      }
    }
  })

  const latestJob = data?.fetchPrintJobs?.printJobs?.[0] ?? null
  const status = latestJob?.status ?? null
  const isBusy = status === 'REQUESTED' || status === 'IN_PROGRESS'

  const [createPrintJob, { loading: creating }] = useCreatePrintJobMutation({
    onError: e => Alert.error(e.message),
    onCompleted: res => {
      const msg = res?.createPrintJob?.response?.message
      Alert.success(msg || 'PDF generation started')
      refetch()
    }
  })

  const [downloadPrintJob, { loading: downloading }] = useDownloadPrintJobLazyQuery({
    fetchPolicy: 'network-only',
    onError: e => Alert.error(e.message),
    onCompleted: res => {
      const url = res?.downloadPrintJob?.preSignedUrl
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    }
  })

  // Poll while a job is queued or in progress so the widget updates itself.
  useEffect(() => {
    if (isBusy) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => refetch(), POLL_INTERVAL_MS)
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [isBusy, refetch])

  const handleGenerate = () => {
    createPrintJob({
      variables: { input: { printJobType: PrintJobType.PrintBatchAll, batchId } }
    })
  }

  const handleView = () => {
    if (latestJob?.id) downloadPrintJob({ variables: { id: latestJob.id } })
  }

  const generateLabel =
    status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED'
      ? 'Regenerate'
      : isBusy
      ? 'Generating…'
      : 'Generate PDF'

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: 2,
        py: 1.25,
        width: 420,
        maxWidth: '100%',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <PdfIcon fontSize="small" color="error" />
        <Typography variant="subtitle2" fontWeight={600}>
          Batch PDF
        </Typography>
        {status && (
          <Chip
            size="small"
            label={STATUS_LABELS[status] ?? status}
            sx={{
              ml: 'auto',
              height: 20,
              color: '#fff',
              bgcolor: STATUS_COLORS[status] ?? '#9E9E9E',
              '& .MuiChip-label': { px: 1, fontSize: 11 }
            }}
          />
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        Generate a combined PDF of all bills in this batch, exactly as sent to customers.
      </Typography>

      {latestJob && (
        <Typography variant="caption" color={status === 'FAILED' ? 'error' : 'text.secondary'}>
          {isBusy
            ? `Processing ${latestJob.processedBills ?? 0}/${totalBills} bills…`
            : status === 'COMPLETED'
            ? `${totalBills} bills · generated ${formatDateTime(latestJob.createdAt)}`
            : status === 'FAILED'
            ? latestJob.errorMessage || 'Generation failed'
            : ''}
        </Typography>
      )}

      <Box display="flex" gap={1} flexWrap="wrap">
        {status === 'COMPLETED' && (
          <Button
            size="small"
            variant="contained"
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <ViewIcon />}
            disabled={downloading}
            onClick={handleView}
          >
            View PDF
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={isBusy || creating ? <CircularProgress size={14} /> : <RefreshIcon />}
          disabled={isBusy || creating}
          onClick={handleGenerate}
        >
          {generateLabel}
        </Button>
      </Box>
    </Box>
  )
}
